import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { BadgePreview } from "../components/BadgePreview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Upload, ArrowLeft, Save, Loader2, X, Building2, Star, Crop as CropIcon, Undo2 } from "lucide-react";
import { PhotoCropDialog } from "../components/PhotoCropDialog";
import { Checkbox } from "../components/ui/checkbox";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";

const empty = {
    cognome: "",
    nome: "",
    codice_fiscale: "",
    data_nascita: "",
    luogo_nascita: "",
    qualifica: "",
    data_assunzione: "",
    matricola: "",
    contratto_tipo: "",
    contratto_ore: "",
    contratto_scadenza: "",
    indirizzo: "",
    citta: "",
    cap: "",
    provincia: "",
    pos_inail: "",
    pos_inps: "",
    cell_personale: "",
    telefono_fisso: "",
    cell_aziendale: "",
    email: "",
    photo: null,
    photo_original: null,
    cliente_logo: null,
    qualifica_evidenza: false,
    group_ids: [],
};

const CONTRATTO_TIPI = [
    "Full-Time Tempo Indeterminato",
    "Full-Time Tempo Determinato",
    "Part-Time Tempo Indeterminato",
    "Part-Time Tempo Determinato",
    "Intermittente Non Definito Determinato",
    "Apprendistato",
    "Tirocinio",
    "Collaborazione Coordinata e Continuativa",
];

// Resize image to max 600px wide and convert to base64 JPEG
const resizeAndEncodeImage = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const img = new Image();
            img.onerror = reject;
            img.onload = () => {
                const maxW = 600;
                const scale = Math.min(1, maxW / img.width);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL("image/jpeg", 0.85));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });

// Resize source data URL to fit within (maxW x maxH) keeping aspect ratio, no upscale.
// Used to compress the user's original upload before storing it as photo_original.
const resizeDataUrl = (dataUrl, maxW = 1500, maxH = 2000, quality = 0.92) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
            const ratio = Math.min(1, maxW / img.width, maxH / img.height);
            const w = Math.round(img.width * ratio);
            const h = Math.round(img.height * ratio);
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.src = dataUrl;
    });

export default function EmployeeForm() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();

    const [form, setForm] = useState(empty);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [groups, setGroups] = useState([]);
    const [cropOpen, setCropOpen] = useState(false);
    const [cropSrc, setCropSrc] = useState(null);
    const fileInputRef = useRef(null);
    const clienteLogoInputRef = useRef(null);

    useEffect(() => {
        // Fetch groups list once (used by the multi-select)
        (async () => {
            try {
                const { data } = await api.get("/groups");
                setGroups(data);
            } catch (e) {
                // Groups are optional — log for diagnostics only
                console.warn("Caricamento gruppi fallito:", e?.message);
            }
        })();
    }, []);

    useEffect(() => {
        if (!isEdit) {
            // New employee: auto-fetch next available matricola
            (async () => {
                try {
                    const { data } = await api.get("/employees/next-matricola");
                    if (data?.next) {
                        setForm((f) => ({ ...f, matricola: data.next }));
                    }
                } catch (e) {
                    // User can still fill manually — log for diagnostics only
                    console.warn("Suggerimento matricola fallito:", e?.message);
                }
            })();
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(`/employees/${id}`);
                setForm({
                    cognome: data.cognome || "",
                    nome: data.nome || "",
                    codice_fiscale: data.codice_fiscale || "",
                    data_nascita: data.data_nascita || "",
                    luogo_nascita: data.luogo_nascita || "",
                    qualifica: data.qualifica || "",
                    data_assunzione: data.data_assunzione || "",
                    matricola: data.matricola || "",
                    contratto_tipo: data.contratto_tipo || "",
                    contratto_ore: data.contratto_ore != null ? String(data.contratto_ore) : "",
                    contratto_scadenza: data.contratto_scadenza || "",
                    indirizzo: data.indirizzo || "",
                    citta: data.citta || "",
                    cap: data.cap || "",
                    provincia: data.provincia || "",
                    pos_inail: data.pos_inail || "",
                    pos_inps: data.pos_inps || "",
                    cell_personale: data.cell_personale || "",
                    telefono_fisso: data.telefono_fisso || "",
                    cell_aziendale: data.cell_aziendale || "",
                    email: data.email || "",
                    photo: data.photo || null,
                    photo_original: data.photo_original || null,
                    cliente_logo: data.cliente_logo || null,
                    qualifica_evidenza: !!data.qualifica_evidenza,
                    group_ids: data.group_ids || [],
                });
            } catch (e) {
                toast.error("Errore nel caricamento");
                navigate("/dipendenti");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isEdit, navigate]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handlePhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Carica un file immagine");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Immagine troppo grande (max 8 MB)");
            return;
        }
        // Open the crop dialog first — user finalizes the photo via crop interface.
        // The full (uncropped) image is also resized and stored as `photo_original`
        // so the admin can restore it later if the crop is wrong.
        try {
            const reader = new FileReader();
            reader.onload = async () => {
                const rawDataUrl = reader.result;
                try {
                    const compressedOriginal = await resizeDataUrl(rawDataUrl, 1500, 2000, 0.92);
                    setForm((f) => ({ ...f, photo_original: compressedOriginal }));
                    setCropSrc(rawDataUrl);
                    setCropOpen(true);
                } catch (err2) {
                    toast.error("Impossibile elaborare l'immagine");
                }
            };
            reader.readAsDataURL(file);
        } catch (err) {
            toast.error("Impossibile caricare l'immagine");
        }
        // Reset file input so re-selecting the same file works
        e.target.value = "";
    };

    const handleCropExisting = () => {
        if (!form.photo) return;
        // If no original is stored yet (e.g. legacy employee), snapshot the current
        // photo as the original BEFORE the new crop, so "Ripristina" still works.
        if (!form.photo_original) {
            setForm((f) => ({ ...f, photo_original: f.photo }));
        }
        setCropSrc(form.photo_original || form.photo);
        setCropOpen(true);
    };

    const handleCropSave = (dataUrl) => {
        setField("photo", dataUrl);
        setCropOpen(false);
        setCropSrc(null);
        toast.success("Foto ritagliata");
    };

    const handleRestoreOriginal = () => {
        if (!form.photo_original) return;
        setField("photo", form.photo_original);
        toast.success("Foto originale ripristinata");
    };

    const handleClienteLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Carica un file immagine");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Immagine troppo grande (max 8 MB)");
            return;
        }
        try {
            const dataUrl = await resizeAndEncodeImage(file);
            setField("cliente_logo", dataUrl);
        } catch (err) {
            toast.error("Impossibile caricare il logo cliente");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Required fields validation
        const required = [
            "cognome", "nome", "data_nascita", "qualifica",
            "data_assunzione", "matricola", "pos_inail", "pos_inps",
        ];
        for (const k of required) {
            if (!form[k] || !String(form[k]).trim()) {
                setError("Compila tutti i campi obbligatori.");
                return;
            }
        }

        setSaving(true);
        try {
            // Convert/clean optional fields
            const payload = { ...form };
            payload.contratto_ore = form.contratto_ore !== "" && form.contratto_ore != null
                ? Number(form.contratto_ore)
                : null;
            ["codice_fiscale", "luogo_nascita", "contratto_tipo", "contratto_scadenza", "indirizzo", "citta", "cap", "provincia", "cell_personale", "telefono_fisso", "cell_aziendale", "email"].forEach((k) => {
                if (!payload[k]) payload[k] = null;
            });

            if (isEdit) {
                await api.put(`/employees/${id}`, payload);
                toast.success("Dipendente aggiornato");
            } else {
                await api.post(`/employees`, payload);
                toast.success("Dipendente creato");
            }
            navigate("/dipendenti");
        } catch (err) {
            const detail = err.response?.data?.detail || "Errore durante il salvataggio";
            const msg = typeof detail === "string" ? detail : "Errore durante il salvataggio";
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div>
                <div className="flex h-[60vh] items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
            </div>
        );
    }

    return (
        <div data-testid="employee-form-page">
            <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <Link
                    to="/dipendenti"
                    className="mb-4 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#376DB3]"
                    data-testid="back-link"
                >
                    <ArrowLeft className="h-4 w-4" /> Torna ai dipendenti
                </Link>

                <div className="mb-8">
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        {isEdit ? "Modifica" : "Nuovo dipendente"}
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#376DB3] sm:text-4xl">
                        {isEdit ? `${form.cognome} ${form.nome}`.trim() || "Modifica dipendente" : "Aggiungi un dipendente"}
                    </h1>
                </div>

                <form onSubmit={handleSubmit} className="grid gap-8 lg:grid-cols-3" data-testid="employee-form">
                    {/* Form fields */}
                    <div className="space-y-6 lg:col-span-2">
                        <Section title="Dati anagrafici">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Field label="Cognome" required>
                                    <Input
                                        value={form.cognome}
                                        onChange={(e) => setField("cognome", e.target.value)}
                                        data-testid="field-cognome"
                                        required
                                    />
                                </Field>
                                <Field label="Nome" required>
                                    <Input
                                        value={form.nome}
                                        onChange={(e) => setField("nome", e.target.value)}
                                        data-testid="field-nome"
                                        required
                                    />
                                </Field>
                                <Field label="Codice Fiscale">
                                    <Input
                                        value={form.codice_fiscale}
                                        onChange={(e) => setField("codice_fiscale", e.target.value.toUpperCase())}
                                        placeholder="es. RSSMRA80A01H501Z"
                                        maxLength={16}
                                        className="font-mono uppercase"
                                        data-testid="field-codice-fiscale"
                                    />
                                </Field>
                                <Field label="Data di nascita" required>
                                    <DatePicker
                                        value={form.data_nascita}
                                        onChange={(v) => setField("data_nascita", v)}
                                        placeholder="Seleziona data di nascita"
                                        fromYear={1940}
                                        toYear={new Date().getFullYear()}
                                        defaultMonth={new Date(1985, 0, 1)}
                                        data-testid="field-data-nascita"
                                    />
                                </Field>
                                <Field label="Luogo di nascita">
                                    <Input
                                        value={form.luogo_nascita}
                                        onChange={(e) => setField("luogo_nascita", e.target.value)}
                                        placeholder="es. Taranto"
                                        data-testid="field-luogo-nascita"
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section title="Dati lavorativi">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Field label="Qualifica" required>
                                    <Input
                                        value={form.qualifica}
                                        onChange={(e) => setField("qualifica", e.target.value)}
                                        placeholder="es. Addetto alle pulizie"
                                        data-testid="field-qualifica"
                                        required
                                    />
                                </Field>
                                <Field label="Data di assunzione" required>
                                    <DatePicker
                                        value={form.data_assunzione}
                                        onChange={(v) => setField("data_assunzione", v)}
                                        placeholder="Seleziona data di assunzione"
                                        fromYear={1970}
                                        data-testid="field-data-assunzione"
                                    />
                                </Field>
                                <Field label="Matricola" required>
                                    <Input
                                        value={form.matricola}
                                        onChange={(e) => setField("matricola", e.target.value)}
                                        placeholder="es. 1640"
                                        data-testid="field-matricola"
                                        required
                                    />
                                    {!isEdit && (
                                        <p className="mt-1 text-xs text-slate-500">
                                            Matricola suggerita automaticamente (prima disponibile dopo COSTA MARIA). Modificabile.
                                        </p>
                                    )}
                                </Field>
                                <Field label="Tipo contratto">
                                    <Input
                                        list="contratto-tipi"
                                        value={form.contratto_tipo}
                                        onChange={(e) => setField("contratto_tipo", e.target.value)}
                                        placeholder="es. Part-Time Tempo Indeterminato"
                                        data-testid="field-contratto-tipo"
                                    />
                                    <datalist id="contratto-tipi">
                                        {CONTRATTO_TIPI.map((t) => (
                                            <option key={t} value={t} />
                                        ))}
                                    </datalist>
                                </Field>
                                <Field label="Ore settimanali">
                                    <Input
                                        type="number"
                                        step="0.25"
                                        min="0"
                                        max="48"
                                        value={form.contratto_ore}
                                        onChange={(e) => setField("contratto_ore", e.target.value)}
                                        placeholder="es. 30"
                                        data-testid="field-contratto-ore"
                                    />
                                </Field>
                                {/^.*\bDeterminato\b/i.test(form.contratto_tipo) && (
                                    <Field label="Scadenza contratto">
                                        <DatePicker
                                            value={form.contratto_scadenza}
                                            onChange={(v) => setField("contratto_scadenza", v)}
                                            placeholder="Seleziona la scadenza"
                                            fromYear={new Date().getFullYear() - 2}
                                            toYear={new Date().getFullYear() + 5}
                                            data-testid="field-contratto-scadenza"
                                        />
                                        <p className="mt-1 text-xs text-amber-700">
                                            Riceverai un avviso 10 giorni prima della scadenza.
                                        </p>
                                    </Field>
                                )}
                            </div>
                            <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 transition-colors hover:border-[#376DB3]/50 hover:bg-[#5BAEDB]/10">
                                <Checkbox
                                    checked={form.qualifica_evidenza}
                                    onCheckedChange={(v) => setField("qualifica_evidenza", !!v)}
                                    className="mt-0.5"
                                    data-testid="field-qualifica-evidenza"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 text-sm font-medium text-[#376DB3]">
                                        <Star className="h-4 w-4" />
                                        Qualifica in evidenza
                                    </div>
                                    <p className="mt-0.5 text-xs text-slate-600">
                                        Mostra la qualifica come pillola in evidenza sul tesserino (utile per ruoli di responsabilità: capo squadra, RSPP, ecc.).
                                    </p>
                                </div>
                            </label>
                        </Section>

                        <Section title="Residenza / Domicilio">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Field label="Indirizzo">
                                    <Input
                                        value={form.indirizzo}
                                        onChange={(e) => setField("indirizzo", e.target.value)}
                                        placeholder="es. Via Roma, 12"
                                        data-testid="field-indirizzo"
                                    />
                                </Field>
                                <Field label="Città">
                                    <Input
                                        value={form.citta}
                                        onChange={(e) => setField("citta", e.target.value)}
                                        placeholder="es. Taranto"
                                        data-testid="field-citta"
                                    />
                                </Field>
                                <Field label="CAP">
                                    <Input
                                        value={form.cap}
                                        onChange={(e) => setField("cap", e.target.value.replace(/\D/g, "").slice(0, 5))}
                                        placeholder="es. 74121"
                                        inputMode="numeric"
                                        maxLength={5}
                                        className="font-mono-numbers"
                                        data-testid="field-cap"
                                    />
                                </Field>
                                <Field label="Provincia">
                                    <Input
                                        value={form.provincia}
                                        onChange={(e) => setField("provincia", e.target.value.toUpperCase().slice(0, 2))}
                                        placeholder="es. TA"
                                        maxLength={2}
                                        className="font-mono uppercase"
                                        data-testid="field-provincia"
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section title="Contatti">
                            <div className="grid gap-4 sm:grid-cols-3">
                                <Field label="Cellulare personale">
                                    <Input
                                        type="tel"
                                        value={form.cell_personale}
                                        onChange={(e) => setField("cell_personale", e.target.value)}
                                        placeholder="es. 333 1234567"
                                        className="font-mono-numbers"
                                        data-testid="field-cell-personale"
                                    />
                                </Field>
                                <Field label="Telefono fisso">
                                    <Input
                                        type="tel"
                                        value={form.telefono_fisso}
                                        onChange={(e) => setField("telefono_fisso", e.target.value)}
                                        placeholder="es. 099 1234567"
                                        className="font-mono-numbers"
                                        data-testid="field-telefono-fisso"
                                    />
                                </Field>
                                <Field label="Cellulare aziendale">
                                    <Input
                                        type="tel"
                                        value={form.cell_aziendale}
                                        onChange={(e) => setField("cell_aziendale", e.target.value)}
                                        placeholder="es. 348 9876543"
                                        className="font-mono-numbers"
                                        data-testid="field-cell-aziendale"
                                    />
                                </Field>
                                <Field label="Email" hint="Usata anche come username del portale">
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setField("email", e.target.value)}
                                        placeholder="es. nome.cognome@gmail.com"
                                        data-testid="field-email"
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section title="Posizioni contributive">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Field label="Pos. INAIL TA" required>
                                    <Input
                                        value={form.pos_inail}
                                        onChange={(e) => setField("pos_inail", e.target.value)}
                                        placeholder="es. 071019791"
                                        data-testid="field-pos-inail"
                                        required
                                    />
                                </Field>
                                <Field label="Pos. INPS TA" required>
                                    <Input
                                        value={form.pos_inps}
                                        onChange={(e) => setField("pos_inps", e.target.value)}
                                        placeholder="es. 7800820454"
                                        data-testid="field-pos-inps"
                                        required
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section title="Gruppi">
                            <p className={`mb-2 text-xs text-slate-500 ${groups.length === 0 ? "" : "hidden"}`}>
                                Nessun gruppo disponibile. Crea i gruppi dalla sezione "Gruppi" nel menu.
                            </p>
                            <div className={`flex flex-wrap gap-2 ${groups.length > 0 ? "" : "hidden"}`} data-testid="groups-multiselect">
                                {groups.map((g) => {
                                    const selected = (form.group_ids || []).includes(g.id);
                                    return (
                                        <button
                                            type="button"
                                            key={g.id}
                                            onClick={() => {
                                                const current = form.group_ids || [];
                                                const next = selected
                                                    ? current.filter((x) => x !== g.id)
                                                    : [...current, g.id];
                                                setField("group_ids", next);
                                            }}
                                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                                                selected
                                                    ? "border-transparent text-white shadow-sm"
                                                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                            }`}
                                            style={selected ? { backgroundColor: g.color || "#376DB3" } : {}}
                                            data-testid={`group-chip-${g.id}`}
                                        >
                                            <span className={`h-2 w-2 rounded-full ${selected ? "bg-white/80" : ""}`} style={!selected ? { backgroundColor: g.color || "#376DB3" } : {}} />
                                            {g.name}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                                Il gruppo <b>DIPENDENTI</b> viene aggiunto automaticamente in fase di creazione.
                            </p>
                        </Section>

                        <Section title="Fotografia">
                            <div className="flex flex-col items-start gap-4 sm:flex-row">
                                <div className="flex h-32 w-24 items-center justify-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50">
                                    {form.photo ? (
                                        <img
                                            src={form.photo}
                                            alt="Anteprima foto"
                                            className="h-full w-full object-cover"
                                            data-testid="photo-preview"
                                        />
                                    ) : (
                                        <span className="px-2 text-center text-[10px] uppercase tracking-wide text-slate-400">
                                            Nessuna foto
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        className="hidden"
                                        onChange={handlePhoto}
                                        data-testid="photo-file-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="gap-2"
                                        data-testid="photo-upload-button"
                                    >
                                        <Upload className="h-4 w-4" />
                                        {form.photo ? "Sostituisci foto" : "Carica foto"}
                                    </Button>
                                    {form.photo && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleCropExisting}
                                            className="gap-2 border-[#376DB3] text-[#376DB3] hover:bg-[#376DB3]/5"
                                            data-testid="photo-crop-button"
                                        >
                                            <CropIcon className="h-4 w-4" /> Ritaglia foto
                                        </Button>
                                    )}
                                    {form.photo_original && form.photo_original !== form.photo && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleRestoreOriginal}
                                            className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                                            data-testid="photo-restore-original-button"
                                            title="Ripristina la foto come era stata caricata (annulla il ritaglio)"
                                        >
                                            <Undo2 className="h-4 w-4" /> Ripristina originale
                                        </Button>
                                    )}
                                    {form.photo && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => setField("photo", null)}
                                            className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                                            data-testid="photo-remove-button"
                                        >
                                            <X className="h-4 w-4" /> Rimuovi
                                        </Button>
                                    )}
                                    <p className="text-xs text-slate-500">
                                        JPG, PNG. Max 8 MB. L'editor di ritaglio rispetta l'esatta area foto del tesserino (22:25): la foto non verrà schiacciata.
                                    </p>
                                </div>
                            </div>
                        </Section>

                        <Section title="Logo cliente (opzionale)">
                            <div className="flex flex-col items-start gap-4 sm:flex-row">
                                <div className="flex h-20 w-32 items-center justify-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50 p-2">
                                    {form.cliente_logo ? (
                                        <img
                                            src={form.cliente_logo}
                                            alt="Anteprima logo cliente"
                                            className="max-h-full max-w-full object-contain"
                                            data-testid="cliente-logo-preview"
                                        />
                                    ) : (
                                        <span className="text-center text-[10px] uppercase tracking-wide text-slate-400">
                                            Nessun logo
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={clienteLogoInputRef}
                                        className="hidden"
                                        onChange={handleClienteLogo}
                                        data-testid="cliente-logo-file-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => clienteLogoInputRef.current?.click()}
                                        className="gap-2"
                                        data-testid="cliente-logo-upload-button"
                                    >
                                        <Building2 className="h-4 w-4" />
                                        {form.cliente_logo ? "Sostituisci logo" : "Carica logo cliente"}
                                    </Button>
                                    {form.cliente_logo && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => setField("cliente_logo", null)}
                                            className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                                            data-testid="cliente-logo-remove-button"
                                        >
                                            <X className="h-4 w-4" /> Rimuovi
                                        </Button>
                                    )}
                                    <p className="text-xs text-slate-500">
                                        Logo dell'azienda cliente / sito di lavoro. Apparirà in alto a sinistra del tesserino.
                                        Se il dipendente appartiene a un gruppo con logo cliente, nella stampa prevale il logo del gruppo.
                                    </p>
                                </div>
                            </div>
                        </Section>

                        {error && (
                            <div
                                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                                data-testid="form-error"
                            >
                                {error}
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <Link to="/dipendenti">
                                <Button type="button" variant="outline" data-testid="form-cancel-button">
                                    Annulla
                                </Button>
                            </Link>
                            <Button
                                type="submit"
                                disabled={saving}
                                className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="form-submit-button"
                            >
                                {saving ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4" />
                                )}
                                {isEdit ? "Aggiorna" : "Crea tesserino"}
                            </Button>
                        </div>
                    </div>

                    {/* Live preview */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24">
                            <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-slate-500">
                                Anteprima Live
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
                                <div className="mx-auto" style={{ width: 324 }}>
                                    <BadgePreview employee={form} />
                                </div>
                                <p className="mt-4 text-center text-xs text-slate-500">
                                    Formato standard 85,6 × 53,98 mm
                                </p>
                            </div>
                        </div>
                    </div>
                </form>
            </main>
            <PhotoCropDialog
                open={cropOpen}
                src={cropSrc}
                onClose={() => { setCropOpen(false); setCropSrc(null); }}
                onSave={handleCropSave}
            />
        </div>
    );
}

const Section = ({ title, children }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-wide text-[#376DB3]">
            {title}
        </h3>
        {children}
    </div>
);

const Field = ({ label, required, hint, children }) => (
    <div className="space-y-1.5">
        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
        </Label>
        {children}
        {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
    </div>
);
